import { UseMutationResult, useMutation, useQueryClient } from "@tanstack/react-query";
import { authService } from "./auth.service";
import type {
  CreateAdminDto,
  LoginDto,
  ChangePasswordDto,
  ForgetPasswordDto,
  ResetPasswordDto,
  AuthResponse,
  MessageResponse,
  AdminUser,
} from "./auth.type";

export enum KeyQueryAuth {
  getCurrentAdmin = "getCurrentAdmin",
}

// Variable pour éviter les appels multiples de déconnexion
let isLoggingOut = false;

// Fonction utilitaire pour la déconnexion directe
export const logout = async (navigate?: (path: string) => void): Promise<void> => {
  // Éviter les appels multiples
  if (isLoggingOut) {
    return;
  }

  isLoggingOut = true;

  try {
    // Tentative de déconnexion côté serveur seulement si on a un token
    const currentToken = localStorage.getItem("accessToken");
    if (currentToken) {
      await authService.logout();
    }
  } catch (error) {
    // Même si la déconnexion serveur échoue, on continue avec la déconnexion locale
    console.warn(
      "Erreur lors de la déconnexion serveur, mais déconnexion locale effectuée:",
      error
    );
  } finally {
    // Nettoyage local (toujours effectué même en cas d'erreur)
    localStorage.removeItem("accessToken");
    localStorage.removeItem("idToken");
    localStorage.removeItem("tokenExpiresIn");
    localStorage.removeItem("userId");

    // Nettoyer les headers axios
    try {
      const api = await import("config/axios.config");
      delete api.default.defaults.headers.common["Authorization"];
    } catch (error) {
      console.warn("Erreur lors du nettoyage des headers axios:", error);
    }

    // Redirection vers la page de connexion seulement si pas déjà sur une page d'auth
    const currentPath = window.location.pathname;
    if (!currentPath.startsWith("/authentication")) {
      if (navigate) {
        navigate("/authentication/sign-in");
      } else {
        window.location.href = "/authentication/sign-in";
      }
    }

    isLoggingOut = false;
  }
};

// Hook pour l'inscription
export const useRegister = (): UseMutationResult<AdminUser, Error, CreateAdminDto> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateAdminDto) => authService.register(data),
    onSuccess: (result) => {
      // Mettre à jour le cache avec les données de l'utilisateur
      queryClient.setQueryData([KeyQueryAuth.getCurrentAdmin, result.id], result);
    },
    onError: (error) => {
      console.error("Erreur lors de l'inscription:", error);
    },
  });
};

// Hook pour la connexion
export const useLogin = (): UseMutationResult<AuthResponse, Error, LoginDto> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: LoginDto) => authService.login(data),
    onSuccess: (result) => {
      // Mettre à jour le cache avec les données de l'utilisateur
      queryClient.setQueryData([KeyQueryAuth.getCurrentAdmin, result.admin.id], result.admin);

      // Stocker les tokens avec timestamp d'expiration
      const expirationTime = Date.now() + result.tokens.expiresIn * 1000;
      localStorage.setItem("accessToken", result.tokens.accessToken);
      localStorage.setItem("idToken", result.tokens.idToken);
      localStorage.setItem("tokenExpiresIn", expirationTime.toString());
      localStorage.setItem("userId", result.admin.id);
    },
    onError: (error) => {
      console.error("Erreur lors de la connexion:", error);
    },
  });
};

// Hook pour la déconnexion (amélioré)
export const useLogout = (): UseMutationResult<void, Error, void> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      // Utiliser la fonction utilitaire qui gère les erreurs
      await logout();
    },
    onSuccess: () => {
      // Vider le cache React Query
      queryClient.clear();
    },
    onError: (error) => {
      console.error("Erreur lors de la déconnexion:", error);
      // Même en cas d'erreur, on vide le cache local
      queryClient.clear();
    },
  });
};

// Hook pour vérifier l'email
export const useVerifyEmail = (): UseMutationResult<
  MessageResponse,
  Error,
  { email: string; code: string }
> => {
  return useMutation({
    mutationFn: ({ email, code }) => authService.verifyEmail(email, code),
    onError: (error) => {
      console.error("Erreur lors de la vérification de l'email:", error);
    },
  });
};

// Hook pour renvoyer le code de vérification
export const useResendVerificationCode = (): UseMutationResult<MessageResponse, Error, string> => {
  return useMutation({
    mutationFn: (email: string) => authService.resendVerificationCode(email),
    onError: (error) => {
      console.error("Erreur lors du renvoi du code:", error);
    },
  });
};

// Hook pour rafraîchir le token (manuel)
export const useRefreshToken = (): UseMutationResult<AuthResponse, Error, void> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => authService.refreshToken(),
    onSuccess: (result) => {
      // Mettre à jour le cache avec les données de l'utilisateur
      queryClient.setQueryData([KeyQueryAuth.getCurrentAdmin, result.admin.id], result.admin);

      // Mettre à jour les tokens avec timestamp d'expiration
      const expirationTime = Date.now() + result.tokens.expiresIn * 1000;
      localStorage.setItem("accessToken", result.tokens.accessToken);
      localStorage.setItem("idToken", result.tokens.idToken);
      localStorage.setItem("tokenExpiresIn", expirationTime.toString());
    },
    onError: (error) => {
      console.error("Erreur lors du rafraîchissement du token:", error);
      // Si le rafraîchissement échoue, forcer la déconnexion
      localStorage.removeItem("accessToken");
      localStorage.removeItem("idToken");
      localStorage.removeItem("tokenExpiresIn");
      localStorage.removeItem("userId");
      queryClient.clear();
    },
  });
};

// Hook pour changer le mot de passe
export const useChangePassword = (): UseMutationResult<
  MessageResponse,
  Error,
  ChangePasswordDto
> => {
  return useMutation({
    mutationFn: (data: ChangePasswordDto) => authService.changePassword(data),
    onError: (error) => {
      console.error("Erreur lors du changement de mot de passe:", error);
    },
  });
};

// Hook pour demander une réinitialisation de mot de passe
export const useForgetPassword = (): UseMutationResult<
  MessageResponse,
  Error,
  ForgetPasswordDto
> => {
  return useMutation({
    mutationFn: (data: ForgetPasswordDto) => authService.forgetPassword(data),
    onError: (error) => {
      console.error("Erreur lors de la demande de réinitialisation:", error);
    },
  });
};

// Hook pour réinitialiser le mot de passe
export const useResetPassword = (): UseMutationResult<MessageResponse, Error, ResetPasswordDto> => {
  return useMutation({
    mutationFn: (data: ResetPasswordDto) => authService.resetPassword(data),
    onError: (error) => {
      console.error("Erreur lors de la réinitialisation du mot de passe:", error);
    },
  });
};

// Hook pour mettre à jour l'admin
export const useUpdateAdmin = (): UseMutationResult<AdminUser, Error, Partial<CreateAdminDto>> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: Partial<CreateAdminDto>) => authService.updateAdmin(data),
    onSuccess: (result) => {
      // Mettre à jour le cache avec les nouvelles données
      queryClient.setQueryData([KeyQueryAuth.getCurrentAdmin, result.id], result);

      // Invalider les autres queries liées
      queryClient.invalidateQueries({ queryKey: [KeyQueryAuth.getCurrentAdmin] });
    },
    onError: (error) => {
      console.error("Erreur lors de la mise à jour de l'admin:", error);
    },
  });
};

// Hook pour invalider les queries d'authentification
export const useInvalidateAuth = () => {
  const queryClient = useQueryClient();

  return () => {
    queryClient.invalidateQueries({ queryKey: [KeyQueryAuth.getCurrentAdmin] });
  };
};

// Hook pour obtenir les tokens stockés
export const useTokens = () => {
  return {
    accessToken: localStorage.getItem("accessToken"),
    idToken: localStorage.getItem("idToken"),
    userId: localStorage.getItem("userId"),
  };
};
